/**
 * Combined date + time picker with optional min/max time bounds.
 */

import * as React from 'react';
import { Label } from '@/components/ui/label';
import { Calendar, Clock, ChevronDown, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { format as formatDate, isValid, startOfDay, endOfDay } from 'date-fns';
import { fr } from 'date-fns/locale';
import { createPortal } from 'react-dom';
import { parseLocalDateString } from '@/utils/format-date-utils';

/** Convert a 24h hour to display hours + AM/PM for 12h mode. */
function hour24ToDisplay(
  hour24: number,
  timeFormat: '24h' | '12h',
): { hours: string; period: 'AM' | 'PM' } {
  const period = hour24 >= 12 ? 'PM' : 'AM';
  if (timeFormat !== '12h') {
    return { hours: String(hour24).padStart(2, '0'), period };
  }
  const displayHour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return { hours: String(displayHour).padStart(2, '0'), period };
}

/** Props for {@link DateTimePicker}. */
interface DateTimePickerProps {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  format?: '24h' | '12h';
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  timeStep?: number;
  disablePast?: boolean;
  disableFuture?: boolean;
  minDate?: string;
  maxDate?: string;
  /** HH:MM — hours/minutes before this are unavailable in the picker */
  minTime?: string;
  /** HH:MM — hours/minutes after this are unavailable in the picker */
  maxTime?: string;
  placement?: 'top' | 'bottom' | 'auto';
}

/** Combined date + time picker with optional min/max time bounds. */
export const DateTimePicker: React.FC<DateTimePickerProps> = ({
  label,
  value,
  onChange,
  format = '24h',
  error,
  helperText,
  required,
  disabled,
  className,
  timeStep = 1,
  disablePast = false,
  disableFuture = false,
  minDate,
  maxDate,
  minTime,
  maxTime,
  placement = 'bottom',
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | null>(null);
  const [hours, setHours] = React.useState('09');
  const [minutes, setMinutes] = React.useState('00');
  const [period, setPeriod] = React.useState<'AM' | 'PM'>('AM');
  const [currentMonth, setCurrentMonth] = React.useState(new Date());
  const [popoverPosition, setPopoverPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const dropdownRef = React.useRef<HTMLDialogElement>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const inputId = `datetime-picker-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;

  React.useEffect(() => {
    if (!value) return;
    const dateObj = new Date(value);
    if (!isValid(dateObj)) return;
    setSelectedDate(dateObj);
    setCurrentMonth(dateObj);
    const display = hour24ToDisplay(dateObj.getHours(), format);
    setPeriod(display.period);
    setHours(display.hours);
    setMinutes(String(dateObj.getMinutes()).padStart(2, '0'));
  }, [value, format]);

  const toMinutes = (h: number, m: number) => h * 60 + m;

  const parseBound = (bound?: string): number | null => {
    if (!bound) return null;
    const [bh, bm] = bound.split(':').map((part) => Number.parseInt(part, 10));
    if (Number.isNaN(bh) || Number.isNaN(bm)) return null;
    return toMinutes(bh, bm);
  };

  const clampToAllowedTime = (h: number, m: number): { hour: number; minute: number } => {
    const minBound = parseBound(minTime);
    const maxBound = parseBound(maxTime);
    let total = toMinutes(h, m);
    if (minBound !== null && total < minBound) total = minBound;
    if (maxBound !== null && total > maxBound) total = maxBound;
    return { hour: Math.floor(total / 60), minute: total % 60 };
  };

  const convertDisplayHourTo24 = (h: string, p: 'AM' | 'PM'): number => {
    const hourNum = Number.parseInt(h, 10);
    if (format === '24h') return hourNum;
    if (p === 'PM' && hourNum !== 12) return hourNum + 12;
    if (p === 'AM' && hourNum === 12) return 0;
    return hourNum;
  };

  /** Keep the date and emit the combined ISO value. */
  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    emitChange(date, hours, minutes, period);
  };

  /** Clamp the time to min/max, then emit if a date is set. */
  const handleTimeChange = (newHours: string, newMinutes: string, newPeriod: 'AM' | 'PM') => {
    const hour24 = convertDisplayHourTo24(newHours, newPeriod);
    const clamped = clampToAllowedTime(hour24, Number.parseInt(newMinutes, 10) || 0);
    const steppedMinute =
      timeStep > 1 ? Math.floor(clamped.minute / timeStep) * timeStep : clamped.minute;

    let displayHour = newHours;
    let displayPeriod = newPeriod;
    if (clamped.hour !== hour24) {
      const display = hour24ToDisplay(clamped.hour, format);
      displayPeriod = display.period;
      displayHour = display.hours;
    }

    const displayMinute = String(steppedMinute).padStart(2, '0');
    setHours(displayHour);
    setMinutes(displayMinute);
    setPeriod(displayPeriod);
    if (selectedDate) {
      emitChange(selectedDate, displayHour, displayMinute, displayPeriod);
    }
  };

  /** Clamp time bounds and emit a local ISO datetime. */
  const emitChange = (date: Date, h: string, m: string, p: 'AM' | 'PM') => {
    let hour24 = convertDisplayHourTo24(h, p);
    const minuteNum = Number.parseInt(m, 10) || 0;
    const clamped = clampToAllowedTime(hour24, minuteNum);
    hour24 = clamped.hour;
    const minute = clamped.minute;

    if (hour24 !== convertDisplayHourTo24(h, p) || minute !== minuteNum) {
      const display = hour24ToDisplay(hour24, format);
      setPeriod(display.period);
      setHours(display.hours);
      setMinutes(String(minute).padStart(2, '0'));
    }

    const localDateTime = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      hour24,
      minute,
    );

    onChange?.(localDateTime.toISOString());
  };

  /** Reset date/time and emit an empty value. */
  const handleClear = () => {
    setSelectedDate(null);
    setHours('09');
    setMinutes('00');
    setPeriod('AM');
    onChange?.('');
  };

  /** Fill the current instant (clamped) and close the panel. */
  const handleNow = () => {
    const now = new Date();
    setSelectedDate(now);
    setCurrentMonth(now);
    const clamped = clampToAllowedTime(now.getHours(), now.getMinutes());
    const h = clamped.hour;
    const m = clamped.minute;

    const display = hour24ToDisplay(h, format);
    setPeriod(display.period);
    setHours(display.hours);
    setMinutes(String(m).padStart(2, '0'));
    emitChange(now, String(h).padStart(2, '0'), String(m).padStart(2, '0'), h >= 12 ? 'PM' : 'AM');
    setIsOpen(false);
  };

  React.useEffect(() => {
    /** Close when the click is outside the trigger and popover. */
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (buttonRef.current?.contains(target)) {
        return;
      }

      if (
        target.hasAttribute?.('data-datetime-popover') ||
        target.closest?.('[data-datetime-popover]')
      ) {
        return;
      }

      setIsOpen(false);
    };

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 0);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 400;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let finalPlacement = placement;
      if (placement === 'auto') {
        finalPlacement = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'top' : 'bottom';
      }

      const top =
        finalPlacement === 'top'
          ? rect.top + window.scrollY - dropdownHeight - 6
          : rect.bottom + window.scrollY + 6;

      setPopoverPosition({
        top,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [isOpen, placement]);

  const displayValue = React.useMemo(() => {
    if (!selectedDate) return '';
    const dateStr = formatDate(selectedDate, 'dd/MM/yy');
    const timeStr = format === '12h' ? `${hours}:${minutes} ${period}` : `${hours}:${minutes}`;
    return `${dateStr} ${timeStr}`;
  }, [selectedDate, hours, minutes, period, format]);

  const generateHours = () => {
    const max = format === '24h' ? 23 : 12;
    const start = format === '24h' ? 0 : 1;
    const allHours = Array.from({ length: max - start + 1 }, (_, i) =>
      String(i + start).padStart(2, '0'),
    );

    if (!minTime && !maxTime) return allHours;

    return allHours.filter((h) => {
      const hour24 = convertDisplayHourTo24(h, period);
      const hourStr = String(hour24).padStart(2, '0');
      if (minTime) {
        const [minHour] = minTime.split(':');
        if (hourStr < minHour) return false;
      }
      if (maxTime) {
        const [maxHour] = maxTime.split(':');
        if (hourStr > maxHour) return false;
      }
      return true;
    });
  };

  const generateMinutes = () => {
    const allMinutes = Array.from({ length: Math.floor(60 / timeStep) }, (_, i) =>
      String(i * timeStep).padStart(2, '0'),
    );

    if (!minTime && !maxTime) return allMinutes;

    const hour24 = convertDisplayHourTo24(hours, period);
    const hourStr = String(hour24).padStart(2, '0');

    return allMinutes.filter((m) => {
      if (minTime) {
        const [minHour, minMinute] = minTime.split(':');
        if (hourStr === minHour && m < minMinute) return false;
        if (hourStr < minHour) return false;
      }
      if (maxTime) {
        const [maxHour, maxMinute] = maxTime.split(':');
        if (hourStr === maxHour && m > maxMinute) return false;
        if (hourStr > maxHour) return false;
      }
      return true;
    });
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: { date: Date | null; key: string }[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, key: `pad-${year}-${month}-${i}` });
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), key: `${year}-${month}-${i}` });
    }

    return days;
  };

  const isDateDisabled = (date: Date | null): boolean => {
    if (!date) return true;
    const now = new Date();
    if (disablePast && date < startOfDay(now)) return true;
    if (disableFuture && date > endOfDay(now)) return true;
    if (minDate && date < parseLocalDateString(minDate.slice(0, 10))) return true;
    if (maxDate && date > parseLocalDateString(maxDate.slice(0, 10))) return true;
    return false;
  };

  const isSameDay = (date1: Date | null, date2: Date | null): boolean => {
    if (!date1 || !date2) return false;
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(
      new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth() + (direction === 'next' ? 1 : -1),
      ),
    );
  };

  return (
    <div className={cn('w-full space-y-2', className)}>
      {label && (
        <Label htmlFor={inputId} className={cn('form-label', required && 'form-label-required')}>
          {label}
        </Label>
      )}

      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          id={inputId}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex h-9 w-full items-center justify-between rounded-lg border-[1.5px] px-3 py-2',
            'text-sm font-medium ring-offset-background transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-[var(--color-2-nuance-1)]/50 focus:ring-offset-1',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)]',
            'bg-[rgb(var(--card-rgb)/0.3)]',
            'hover:border-[color-mix(in_srgb,var(--color-1-nuance-2)_70%,transparent)] hover:shadow-sm',
            isOpen && 'border-[var(--color-2-nuance-1)]',
            error && 'border-red-500',
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Calendar className="h-4 w-4 text-[var(--color-1-nuance-1)] shrink-0" />
            <span className={cn('truncate', !displayValue && 'text-muted-foreground/60')}>
              {displayValue || 'Date et heure'}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <ChevronDown
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200',
                'text-[var(--color-1-nuance-1)]',
                isOpen && 'rotate-180',
              )}
            />
          </div>
        </button>

        {value && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="absolute right-8 top-1/2 -translate-y-1/2 h-4 w-4 flex items-center justify-center hover:bg-muted rounded transition-colors z-10"
          >
            <X className="h-3.5 w-3.5 text-[var(--color-2-nuance-2)]" />
          </button>
        )}

        {isOpen &&
          popoverPosition &&
          createPortal(
            <dialog
              ref={dropdownRef}
              open
              data-datetime-popover="true"
              className="rounded-lg border-[1.5px] border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] bg-popover backdrop-blur-lg shadow-xl p-3 pointer-events-auto"
              style={{
                position: 'fixed',
                top: `${popoverPosition.top}px`,
                left: `${popoverPosition.left}px`,
                margin: 0,
                zIndex: 9999,
                minWidth: '420px',
              }}
            >
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-between mb-2">
                    <button
                      type="button"
                      onClick={() => navigateMonth('prev')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                    >
                      <ChevronDown className="h-3.5 w-3.5 rotate-90" />
                    </button>
                    <div className="text-xs font-semibold text-[var(--color-1-nuance-1)]">
                      {formatDate(currentMonth, 'MMM yyyy', { locale: fr })}
                    </div>
                    <button
                      type="button"
                      onClick={() => navigateMonth('next')}
                      className="p-1 hover:bg-muted rounded transition-colors"
                    >
                      <ChevronDown className="h-3.5 w-3.5 -rotate-90" />
                    </button>
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 mb-1">
                    {['D', 'L', 'Ma', 'Me', 'J', 'V', 'S'].map((day) => (
                      <div
                        key={day}
                        className="text-[10px] font-medium text-center text-muted-foreground py-1 w-7"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-0.5">
                    {getDaysInMonth().map(({ date, key }) => {
                      const isDisabled = isDateDisabled(date);
                      const isSelected = isSameDay(date, selectedDate);
                      const isToday = date && isSameDay(date, new Date());

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => date && !isDisabled && handleDateSelect(date)}
                          disabled={isDisabled}
                          className={cn(
                            'h-7 w-7 rounded text-xs transition-all duration-150',
                            !date && 'invisible',
                            isDisabled && 'opacity-30 cursor-not-allowed',
                            !isDisabled && !isSelected && 'hover:bg-muted',
                            isSelected &&
                              'bg-[var(--color-2-nuance-1)] text-white font-semibold shadow-sm',
                            isToday && !isSelected && 'border border-[var(--color-2-nuance-1)]',
                          )}
                        >
                          {date?.getDate()}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex flex-col gap-2 border-l border-border/50 pl-3 w-[140px]">
                  <div className="flex items-center gap-1.5 text-xs font-semibold text-[var(--color-1-nuance-1)]">
                    <Clock className="h-3.5 w-3.5" />
                    Heure
                  </div>

                  <div
                    className={cn('grid gap-1.5', format === '12h' ? 'grid-cols-3' : 'grid-cols-2')}
                  >
                    <div className="flex flex-col">
                      <span className="text-[10px] font-medium text-muted-foreground mb-1 text-center">
                        H
                      </span>
                      <div
                        className="max-h-32 overflow-y-auto scrollbar-thin space-y-0.5"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {generateHours().map((h) => (
                          <button
                            key={h}
                            type="button"
                            onClick={() => {
                              handleTimeChange(h, minutes, period);
                              if (selectedDate && minutes) {
                                setIsOpen(false);
                              }
                            }}
                            className={cn(
                              'w-full py-1 px-1 rounded text-center text-xs transition-all duration-150',
                              hours === h
                                ? 'bg-[var(--color-2-nuance-1)] text-white font-medium'
                                : 'hover:bg-muted',
                            )}
                          >
                            {h}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-col">
                      <span className="text-[10px] font-medium text-muted-foreground mb-1 text-center">
                        M
                      </span>
                      <div
                        className="max-h-32 overflow-y-auto scrollbar-thin space-y-0.5"
                        onWheel={(e) => e.stopPropagation()}
                      >
                        {generateMinutes().map((m) => (
                          <button
                            key={m}
                            type="button"
                            onClick={() => {
                              handleTimeChange(hours, m, period);
                              if (selectedDate && hours) {
                                setIsOpen(false);
                              }
                            }}
                            className={cn(
                              'w-full py-1 px-1 rounded text-center text-xs transition-all duration-150',
                              minutes === m
                                ? 'bg-[var(--color-2-nuance-1)] text-white font-medium'
                                : 'hover:bg-muted',
                            )}
                          >
                            {m}
                          </button>
                        ))}
                      </div>
                    </div>

                    {format === '12h' && (
                      <div className="flex flex-col">
                        <span className="text-[10px] font-medium text-muted-foreground mb-1 text-center">
                          -
                        </span>
                        <div className="space-y-0.5">
                          {['AM', 'PM'].map((p) => (
                            <button
                              key={p}
                              type="button"
                              onClick={() => handleTimeChange(hours, minutes, p as 'AM' | 'PM')}
                              className={cn(
                                'w-full py-1 px-1 rounded text-center text-xs font-medium transition-all duration-150',
                                period === p
                                  ? 'bg-[var(--color-2-nuance-1)] text-white'
                                  : 'hover:bg-muted',
                              )}
                            >
                              {p}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={handleNow}
                    className="mt-auto px-2 py-1.5 text-xs font-medium rounded border border-[var(--color-2-nuance-1)] text-[var(--color-2-nuance-1)] hover:bg-[var(--color-2-nuance-1)] hover:text-white transition-all duration-200"
                  >
                    Maintenant
                  </button>
                </div>
              </div>
            </dialog>,
            document.body,
          )}
      </div>

      {error && <p className="form-error-text">{error}</p>}
      {helperText && !error && <p className="form-helper-text">{helperText}</p>}
    </div>
  );
};
